import {
  QueueCompleteNotAllowed
} from "./chunk-PY7SOKTM.js";

// src/actor/instance/keys.ts
var KEYS = {
  PERSIST_DATA: Uint8Array.from([1]),
  CONN_PREFIX: Uint8Array.from([2]),
  // Prefix for connection keys
  INSPECTOR_TOKEN: Uint8Array.from([3]),
  // Inspector token key
  KV: Uint8Array.from([4]),
  // Prefix for user-facing KV storage
  QUEUE_PREFIX: Uint8Array.from([5]),
  // Prefix for queue message keys
  QUEUE_METADATA: Uint8Array.from([6]),
  // Queue metadata key
  WORKFLOW_PREFIX: Uint8Array.from([7]),
  // Prefix for workflow storage
  TRACES_PREFIX: Uint8Array.from([8])
  // Prefix for traces storage
};
var QUEUE_ID_BYTES = 8;
function makePrefixedKey(key) {
  const prefixed = new Uint8Array(KEYS.KV.length + key.length);
  prefixed.set(KEYS.KV, 0);
  prefixed.set(key, KEYS.KV.length);
  return prefixed;
}
function removePrefixFromKey(prefixedKey) {
  return prefixedKey.slice(KEYS.KV.length);
}
function makeWorkflowKey(key) {
  const prefixed = new Uint8Array(KEYS.WORKFLOW_PREFIX.length + key.length);
  prefixed.set(KEYS.WORKFLOW_PREFIX, 0);
  prefixed.set(key, KEYS.WORKFLOW_PREFIX.length);
  return prefixed;
}
function makeConnKey(connId) {
  const encoder = new TextEncoder();
  const connIdBytes = encoder.encode(connId);
  const key = new Uint8Array(KEYS.CONN_PREFIX.length + connIdBytes.length);
  key.set(KEYS.CONN_PREFIX, 0);
  key.set(connIdBytes, KEYS.CONN_PREFIX.length);
  return key;
}
function makeQueueMessageKey(id) {
  const key = new Uint8Array(KEYS.QUEUE_PREFIX.length + QUEUE_ID_BYTES);
  key.set(KEYS.QUEUE_PREFIX, 0);
  const view = new DataView(key.buffer, key.byteOffset, key.byteLength);
  view.setBigUint64(KEYS.QUEUE_PREFIX.length, id, false);
  return key;
}
function decodeQueueMessageKey(key) {
  const offset = KEYS.QUEUE_PREFIX.length;
  if (key.length < offset + QUEUE_ID_BYTES) {
    throw new Error("Queue key is too short");
  }
  const view = new DataView(
    key.buffer,
    key.byteOffset + offset,
    QUEUE_ID_BYTES
  );
  return view.getBigUint64(0, false);
}

// src/actor/instance/kv.ts
var textEncoder = new TextEncoder();
var textDecoder = new TextDecoder();
function encodeKey(key, keyType) {
  if (key instanceof Uint8Array) {
    return key;
  }
  const resolvedKeyType = keyType ?? "text";
  if (resolvedKeyType === "binary") {
    throw new TypeError("Expected a Uint8Array when keyType is binary");
  }
  return textEncoder.encode(key);
}
function decodeKey(key, keyType) {
  const resolvedKeyType = keyType ?? "text";
  switch (resolvedKeyType) {
    case "text":
      return textDecoder.decode(key);
    case "binary":
      return key;
    default:
      throw new TypeError("Invalid kv key type");
  }
}
function resolveValueType(value) {
  if (typeof value === "string") {
    return "text";
  }
  if (value instanceof Uint8Array) {
    return "binary";
  }
  if (value instanceof ArrayBuffer) {
    return "arrayBuffer";
  }
  throw new TypeError("Invalid kv value");
}
function encodeValue(value, options) {
  const type = (options == null ? void 0 : options.type) ?? resolveValueType(value);
  switch (type) {
    case "text":
      if (typeof value !== "string") {
        throw new TypeError("Expected a string when type is text");
      }
      return textEncoder.encode(value);
    case "arrayBuffer":
      if (!(value instanceof ArrayBuffer)) {
        throw new TypeError("Expected an ArrayBuffer when type is arrayBuffer");
      }
      return new Uint8Array(value);
    case "binary":
      if (!(value instanceof Uint8Array)) {
        throw new TypeError("Expected a Uint8Array when type is binary");
      }
      return value;
    default:
      throw new TypeError("Invalid kv value type");
  }
}
function decodeValue(value, options) {
  const type = (options == null ? void 0 : options.type) ?? "text";
  switch (type) {
    case "text":
      return textDecoder.decode(value);
    case "arrayBuffer": {
      const copy = new Uint8Array(value.byteLength);
      copy.set(value);
      return copy.buffer;
    }
    case "binary":
      return value;
    default:
      throw new TypeError("Invalid kv value type");
  }
}
var ActorKv = class {
  #driver;
  #actorId;
  constructor(driver, actorId) {
    this.#driver = driver;
    this.#actorId = actorId;
  }
  /**
   * Get a single value by key.
   */
  async get(key, options) {
    const results = await this.#driver.kvBatchGet(this.#actorId, [
      makePrefixedKey(encodeKey(key))
    ]);
    const result = results[0];
    if (!result) {
      return null;
    }
    return decodeValue(result, options);
  }
  /**
   * Get multiple values by keys.
   */
  async getBatch(keys, options) {
    const prefixedKeys = keys.map(
      (key) => makePrefixedKey(encodeKey(key))
    );
    const results = await this.#driver.kvBatchGet(
      this.#actorId,
      prefixedKeys
    );
    return results.map(
      (result) => result ? decodeValue(result, options) : null
    );
  }
  /**
   * Put a single key-value pair.
   */
  async put(key, value, options) {
    await this.#driver.kvBatchPut(this.#actorId, [
      [makePrefixedKey(encodeKey(key)), encodeValue(value, options)]
    ]);
  }
  /**
   * Put multiple key-value pairs.
   */
  async putBatch(entries, options) {
    const prefixedEntries = entries.map(
      ([key, value]) => [
        makePrefixedKey(encodeKey(key)),
        encodeValue(value, options)
      ]
    );
    await this.#driver.kvBatchPut(this.#actorId, prefixedEntries);
  }
  /**
   * Delete a single key.
   */
  async delete(key) {
    await this.#driver.kvBatchDelete(this.#actorId, [
      makePrefixedKey(encodeKey(key))
    ]);
  }
  /**
   * Delete multiple keys.
   */
  async deleteBatch(keys) {
    const prefixedKeys = keys.map(
      (key) => makePrefixedKey(encodeKey(key))
    );
    await this.#driver.kvBatchDelete(this.#actorId, prefixedKeys);
  }
  /**
   * List all keys with a given prefix.
   * Returns key-value pairs where keys have the user prefix removed.
   */
  async list(prefix, options) {
    const prefixedPrefix = makePrefixedKey(
      encodeKey(prefix, options == null ? void 0 : options.keyType)
    );
    const results = await this.#driver.kvListPrefix(
      this.#actorId,
      prefixedPrefix
    );
    return results.map(([key, value]) => [
      decodeKey(removePrefixFromKey(key), options == null ? void 0 : options.keyType),
      decodeValue(value, options)
    ]);
  }
};

// src/actor/instance/queue.ts
var ActorQueue = class {
  #queueManager;
  #abortSignal;
  constructor(queueManager, abortSignal) {
    this.#queueManager = queueManager;
    this.#abortSignal = abortSignal;
  }
  async next(nameOrRequest, opts = {}) {
    const request = typeof nameOrRequest === "object" && !Array.isArray(nameOrRequest) ? nameOrRequest : { name: nameOrRequest };
    const mergedOptions = request === nameOrRequest ? request : opts;
    const names = Array.isArray(request.name) ? request.name : [request.name];
    const count = mergedOptions.count ?? 1;
    const messages = await this.#queueManager.receive(
      names,
      count,
      mergedOptions.timeout,
      this.#abortSignal,
      mergedOptions.wait ?? false
    );
    if (Array.isArray(request.name)) {
      return messages == null ? void 0 : messages.map(
        (message) => this.#toQueueMessage(message, mergedOptions.wait ?? false)
      );
    }
    if (!messages || messages.length === 0) {
      return void 0;
    }
    return this.#toQueueMessage(messages[0], mergedOptions.wait ?? false);
  }
  #toQueueMessage(message, wait) {
    const base = {
      id: message.id.toString(),
      name: message.name,
      body: message.body,
      complete: async (data) => {
        if (!wait) {
          throw new QueueCompleteNotAllowed();
        }
        await this.#queueManager.complete(message, data);
      }
    };
    return base;
  }
  /** Sends a message to the specified queue. */
  async send(name, body) {
    const message = await this.#queueManager.enqueue(name, body);
    return this.#toQueueMessage(message, false);
  }
};

// src/actor/contexts/base/actor.ts
var ACTOR_CONTEXT_INTERNAL_SYMBOL = Symbol("actorContextInternal");
var ActorContext = class {
  [ACTOR_CONTEXT_INTERNAL_SYMBOL];
  #actor;
  #kv;
  #queue;
  constructor(actor) {
    this.#actor = actor;
    this[ACTOR_CONTEXT_INTERNAL_SYMBOL] = actor;
  }
  /**
   * Gets the KV storage interface.
   */
  get kv() {
    if (!this.#kv) {
      this.#kv = new ActorKv(this.#actor.driver, this.#actor.id);
    }
    return this.#kv;
  }
  /**
   * Get the actor state
   *
   * @remarks
   * This property is not available in `createState` since the state hasn't been created yet.
   */
  get state() {
    return this.#actor.state;
  }
  /**
   * Get the actor variables
   *
   * @remarks
   * This property is not available in `createVars` since the variables haven't been created yet.
   * Variables are only available if you define `vars` or `createVars` in your actor config.
   */
  get vars() {
    return this.#actor.vars;
  }
  /**
   * Broadcasts an event to all connected clients.
   * @param name - The name of the event.
   * @param args - The arguments to send with the event.
   */
  broadcast(name, ...args) {
    this.#actor.eventManager.broadcast(name, ...args);
    return;
  }
  /**
   * Gets the logger instance.
   */
  get log() {
    return this.#actor.log;
  }
  /**
   * Access to queue receive helpers.
   */
  get queue() {
    if (!this.#queue) {
      this.#queue = new ActorQueue(
        this.#actor.queueManager,
        this.#actor.abortSignal
      );
    }
    return this.#queue;
  }
  /**
   * Gets actor ID.
   */
  get actorId() {
    return this.#actor.id;
  }
  /**
   * Gets the actor name.
   */
  get name() {
    return this.#actor.name;
  }
  /**
   * Gets the actor key.
   */
  get key() {
    return this.#actor.key;
  }
  /**
   * Gets the region.
   */
  get region() {
    return this.#actor.region;
  }
  /**
   * Gets the scheduler.
   */
  get schedule() {
    return this.#actor.schedule;
  }
  /**
   * Gets the map of connections.
   */
  get conns() {
    return this.#actor.conns;
  }
  /**
   * Returns the client for the given registry.
   */
  client() {
    return this.#actor.inlineClient;
  }
  /**
   * Gets the database.
   *
   * @experimental
   * @remarks
   * This property is only available if you define a `db` provider in your actor config.
   * @throws {DatabaseNotEnabled} If the database is not enabled.
   */
  get db() {
    return this.#actor.db;
  }
  /**
   * Forces the state to get saved.
   *
   * @param opts - Options for saving the state.
   */
  async saveState(opts) {
    return this.#actor.stateManager.saveState(opts);
  }
  /**
   * Prevents the actor from sleeping until promise is complete.
   */
  waitUntil(promise) {
    this.#actor.waitUntil(promise);
  }
  /**
   * Prevents the actor from sleeping until promise is complete.
   * Returns the resolved value and resets sleep timer on completion.
   * Unlike waitUntil, errors are propagated to the caller.
   */
  keepAwake(promise) {
    return this.#actor.keepAwake(promise);
  }
  /**
   * AbortSignal that fires when the actor is stopping.
   */
  get abortSignal() {
    return this.#actor.abortSignal;
  }
  /**
   * Forces the actor to sleep.
   *
   * Not supported on all drivers.
   *
   * @experimental
   */
  sleep() {
    this.#actor.startSleep();
  }
  /**
   * Forces the actor to destroy.
   *
   * This will return immediately, then call `onStop` and `onDestroy`.
   *
   * @experimental
   */
  destroy() {
    this.#actor.startDestroy();
  }
};

export {
  KEYS,
  makeWorkflowKey,
  makeConnKey,
  makeQueueMessageKey,
  decodeQueueMessageKey,
  ActorKv,
  ACTOR_CONTEXT_INTERNAL_SYMBOL,
  ActorContext
};
//# sourceMappingURL=chunk-7LY4X5LS.js.map